import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUrlString } from '../../hooks/useUrlState';
import { Boxes, AlertTriangle, FlaskConical, Ban, Search, ChevronRight, ChevronDown, Wrench, UserCheck, Cog } from 'lucide-react';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore, useCanSeeWarehouses } from '../../store/auth';
import { Pill } from '../../components/TwDataTable';
import { SortableTh } from '../../components/SortableTh';
import { useServerSort } from '../../hooks/useServerSort';
import { Select, Input, StatCard, Pagination, CodeChip, entityHue } from '../../ui';
import type { StatTone } from '../../ui';
import { formatIst } from '../../lib/time';
import { isRedundantSku } from '../../lib/format';
import { warehouseOption } from '../../lib/warehouseLabel';
import { accentForProductName } from '../../lib/productAccent';
import { productImages } from '../../lib/imageUrls';
import { ProductThumb } from '../../components/ProductThumb';
import { CopyValue } from '../../components/CopyValue';
import { inr2 } from '../../lib/format';
import { inr as inrWhole } from '../../lib/format';

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;
const qf = (n: any) => Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const dateOnly = (v: any) => (v ? String(v).substring(0, 10) : null);
// "2020-01-01" is a machine's way of writing a date. Every other register in the app writes
// "01 Jan 2020", and this is the one screen where the reader is judging how old something is.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (v: any) => {
  const d = dateOnly(v);
  if (!d) return null;
  const [y, m, dd] = d.split('-');
  return MONTHS[Number(m) - 1] ? `${dd} ${MONTHS[Number(m) - 1]} ${y}` : d;
};

const dt = (v: any) => (v ? formatIst(v) : null);
// Whole days since a (date-only) value — positive = in the past. Used for "expired N days ago".
const daysSince = (v: any) => { if (!v) return null; const d = Math.floor((Date.now() - new Date(`${String(v).substring(0, 10)}T00:00:00`).getTime()) / 86400000); return Number.isFinite(d) ? d : null; };

/**
 * Why a batch can't be sold → its label, its pill colour, and the RAIL it wears.
 *
 * The reason was said once, in a pill at the far right edge of a wide row — so the register read as
 * one undifferentiated list and you had to track across the whole row to find out which kind of
 * problem you were looking at. The same colour now runs down the row's left edge, which is where the
 * eye starts, so expired / QC-hold / blocked separate at a glance without reading anything.
 */
const REASON_META: Record<string, { label: string; color: string; rail: string; wash: string; card: string }> = {
  EXPIRED: { label: 'Expired', color: 'red', rail: 'border-l-danger-500', wash: 'from-danger-500/[0.07]', card: 'border-danger-500/30 bg-danger-500/[0.05]' },
  QC_HOLD: { label: 'QC hold', color: 'orange', rail: 'border-l-warning-500', wash: 'from-warning-500/[0.07]', card: 'border-warning-500/30 bg-warning-500/[0.05]' },
  BLOCKED: { label: 'Blocked', color: 'purple', rail: 'border-l-violet-500', wash: 'from-violet-500/[0.07]', card: 'border-violet-500/30 bg-violet-500/[0.05]' },
};
const FALLBACK_META = { label: '', color: 'default', rail: 'border-l-line', wash: 'from-transparent', card: 'border-line bg-surface' };

// The original return condition (as classified on receipt) → a human phrase for the provenance line.
const CONDITION_LABEL: Record<string, string> = {
  DAMAGED: 'damaged', DEFECTIVE: 'defective', MISMATCH: 'batch mismatch', EXPIRED: 'expired', BLOCKED: 'blocked', NEAR_EXPIRY: 'near-expiry', RESALABLE: 'resalable',
};

// Per-reason plain-language explanation + what to do about it, shown in the expanded detail.
function reasonDetail(r: any): { what: string; action: string } {
  if (r.reason === 'EXPIRED') {
    const d = daysSince(r.expDate);
    const what = r.expDate
      ? (d != null && d > 0 ? `Past its expiry date — expired ${d} day${d === 1 ? '' : 's'} ago.` : 'Past its expiry date.')
      : 'Marked expired manually (e.g. an expired return booked in by the warehouse).';
    return { what, action: 'Cannot be sold or dispatched — write off / dispose of this batch.' };
  }
  if (r.reason === 'QC_HOLD') return { what: 'Held for quality inspection — flagged damaged, defective, or mismatched on receipt or return.', action: 'Inspect, then reclassify: restock if good, or write off / return to vendor.' };
  return { what: 'Administratively blocked — recall, regulatory hold, or under investigation.', action: 'Awaiting release; returns to sellable stock once unblocked.' };
}

/**
 * Non-sellable / quarantine stock register — every on-hand batch that can't be sold (expired, QC-hold, blocked,
 * or past its expiry date). Reason-categorised with per-reason KPIs. A Warehouse Manager sees only their
 * warehouse(s) and quantities; Admin / Super Admin see all warehouses (with a filter) and value.
 */
export function NonSellableStockPage({ embedded }: { embedded?: boolean } = {}) {
  const canSeeWarehouses = useCanSeeWarehouses();
  const role = useAuthStore((s) => s.user?.role);
  const isWHM = role === 'WAREHOUSE_MANAGER';

  const [reason, setReason] = useUrlString('reason', 'ALL');
  const [warehouseId, setWarehouseId] = useUrlString('wh', 'ALL');
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  const [open, setOpen] = useState<Set<string>>(new Set());
  // PAGING IS LOCAL, because the register is: the endpoint hands back its (capped) set in one call,
  // so a page here is a slice of what is already in the browser. Every filter and the sort re-slice
  // that list, so each sends the reader back to the first page rather than leaving them on a page
  // four that is now somebody else's rows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const go = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };
  const toggle = (id: string) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Sorting is the backend's ORDER BY, not a browser re-sort: the register is capped at 500 rows
  // (the "top 500 by value" note below), so a local sort would only reorder that slice — asking for
  // "oldest expiry first" would hand back the oldest of the 500 biggest, not the oldest overall.
  const { sortState, onSortChange, sort } = useServerSort({ onChange: () => setPage(1) });

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (reason !== 'ALL') p.reason = reason;
    if (warehouseId !== 'ALL') p.warehouseId = warehouseId;
    if (search.trim()) p.search = search.trim();
    if (sort) p.sort = sort;
    return p;
  }, [reason, warehouseId, search, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['non-sellable-stock', params],
    queryFn: () => request<any>({ url: '/stock/non-sellable', params }),
  });
  const rows: any[] = data?.rows ?? [];
  const s = data?.summary ?? { totalUnits: 0, batchCount: 0, totalValue: null, expiredUnits: 0, qcUnits: 0, blockedUnits: 0, expiredValue: null, qcValue: null, blockedValue: null };
  const hasValue = s.totalValue != null;
  const cols = hasValue ? 8 : 7; // chevron + product + batch + warehouse + expiry + qty + [value] + reason

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount);
  const paged = rows.slice((current - 1) * pageSize, current * pageSize);

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-all'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses && !isWHM,
  });

  // A KPI shows value when the caller can see valuation, else the unit count.
  const kpiVal = (value: number | null, units: number) => (hasValue && value != null ? inr(value) : `${qf(units)} u`);

  return (
    <div>
      {!embedded && <h1 className="mb-1 type-page-title text-content">Non-sellable stock</h1>}
      <p className="mb-3 text-sm text-muted">Everything on hand that can't be sold — expired, on QC hold, blocked, or past its expiry date. Not counted in sellable stock or dispatch.</p>

      {/* KPIs by reason — click a card to filter the list to that reason (click again / Total to clear) */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi title="Total non-sellable" value={kpiVal(s.totalValue, s.totalUnits)} sub={`${s.batchCount} batch${s.batchCount === 1 ? '' : 'es'}`} accent="border-l-line" valueCls="text-content" icon={<Boxes size={16} />} active={reason === 'ALL'} onClick={() => go(setReason)('ALL')} />
        <Kpi title="Expired" value={kpiVal(s.expiredValue, s.expiredUnits)} sub="past life / date-expired" accent="border-l-danger-500" valueCls="text-danger-600" icon={<AlertTriangle size={16} />} active={reason === 'EXPIRED'} onClick={() => go(setReason)((r) => (r === 'EXPIRED' ? 'ALL' : 'EXPIRED'))} />
        <Kpi title="QC hold" value={kpiVal(s.qcValue, s.qcUnits)} sub="damaged / mismatch — inspect" accent="border-l-warning-500" valueCls="text-warning-600" icon={<FlaskConical size={16} />} active={reason === 'QC_HOLD'} onClick={() => go(setReason)((r) => (r === 'QC_HOLD' ? 'ALL' : 'QC_HOLD'))} />
        <Kpi title="Blocked" value={kpiVal(s.blockedValue, s.blockedUnits)} sub="recall / regulatory hold" accent="border-l-violet-500" valueCls="text-violet-600" icon={<Ban size={16} />} active={reason === 'BLOCKED'} onClick={() => go(setReason)((r) => (r === 'BLOCKED' ? 'ALL' : 'BLOCKED'))} />
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface shadow-e1 px-3 py-2.5">
        <Select
          value={reason} onChange={go(setReason)} size="sm" className="w-44"
          options={[
            { value: 'ALL', label: 'All reasons' },
            { value: 'EXPIRED', label: 'Expired' },
            { value: 'QC_HOLD', label: 'QC hold' },
            { value: 'BLOCKED', label: 'Blocked' },
          ]}
        />
        {!isWHM && (
          <Select
            value={warehouseId} onChange={go(setWarehouseId)} size="sm" className="w-64"
            options={[{ value: 'ALL', label: 'All warehouses' }, ...(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) }))]}
          />
        )}
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={searchInput} onChange={(e) => go(setSearchInput)(e.target.value)} placeholder="Batch / product / SKU…" size="sm" className="w-56 pl-8" />
        </div>
      </div>

      {/* Table — click a row to expand its full batch detail */}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
          <thead className="gb-thead-tint">
            <tr className="border-b border-line text-left type-overline text-muted">
              <th className="w-8 px-2 py-2.5"></th>
              <SortableTh sortKey="productName" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Product</SortableTh>
              <SortableTh sortKey="batchNo" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Batch</SortableTh>
              <SortableTh sortKey="warehouse" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Warehouse</SortableTh>
              <SortableTh sortKey="expDate" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Expiry</SortableTh>
              <SortableTh sortKey="qty" align="right" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Qty</SortableTh>
              {hasValue && <SortableTh sortKey="value" align="right" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Value</SortableTh>}
              <SortableTh sortKey="reason" state={sortState} onSort={onSortChange} className="px-3 py-2.5">Reason</SortableTh>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={cols} className="px-3 py-10 text-center text-muted">Loading…</td></tr>}
            {!isLoading && !rows.length && <tr><td colSpan={cols} className="px-3 py-10 text-center text-muted">No non-sellable stock 🎉</td></tr>}
            {paged.map((r) => {
              const meta = REASON_META[r.reason] ?? { ...FALLBACK_META, label: r.reason };
              const isOpen = open.has(r.batchId);
              const accent = accentForProductName(r.productName);
              return (
                <Fragment key={r.batchId}>
                  <tr
                    className={`cursor-pointer border-b border-line/70 border-l-[3px] ${meta.rail} ${isOpen ? `bg-linear-to-r ${meta.wash} to-transparent to-55%` : 'hover:bg-surface-2'}`}
                    onClick={() => toggle(r.batchId)}
                  >
                    <td className="px-2 py-2.5 text-muted">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        {/* The pack shot, openable. Two initials say WHICH row this is; they do not
                            say what is in the drum. A held lot is a thing someone has to walk to a
                            rack and identify, so the mark is the real photo and clicking it opens
                            the full-size viewer. No photo on file → initials on the product's own
                            packaging colour (lib/productAccent), the hue it wears in the stock table
                            and the valuation chart. */}
                        <ProductThumb name={r.productName} srcs={productImages(r)} hue={accent} size={32} />
                        <div className="min-w-0">
                          <div className="font-medium text-content">{r.productName || '—'}</div>
                          {r.sku && !isRedundantSku(r.sku, r.productName) && <div className="text-xs text-subtle">{r.sku}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <CopyValue value={r.batchNo} label="Batch no"><CodeChip>{r.batchNo}</CodeChip></CopyValue>
                      {r.sourceBatchNo && <div className="mt-0.5 type-caption text-subtle" title="The original batch this lot came from">↩ from {r.sourceBatchNo}</div>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {r.warehouseCode
                        ? <span className="flex min-w-0 items-center gap-1.5"><CodeChip hue={entityHue(r.warehouseCode)} className="shrink-0">{r.warehouseCode}</CodeChip><span className="min-w-0 truncate type-label text-muted">{r.warehouseName}</span></span>
                        : <span className="text-subtle">{r.warehouseName ?? '—'}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-muted">
                      {/* A lot with no expiry on file is not a blank — it is a lot whose shelf life
                          nobody can check. Named in amber rather than left as an em-dash.
                          NO "past" badge beside the date. It said the date was before today, which
                          is what the date already says, on rows whose Reason column already says
                          Expired — a red mark printed on the rows that were labelled anyway. */}
                      {fmtDate(r.expDate) ?? <span className="type-label text-warning-600 dark:text-harvest-300">no expiry on file</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-content">{qf(r.qty)}</td>
                    {hasValue && <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-content">{inr(r.value)}</td>}
                    <td className="px-3 py-2.5"><Pill color={meta.color}>{meta.label}</Pill></td>
                  </tr>
                  {isOpen && (
                    <tr className="border-y border-line bg-surface-2">
                      <td colSpan={cols} className="p-0">
                        {/* The expansion keeps the reason's rail, not a generic brand one, so the
                            detail block stays visibly attached to the row that opened it. */}
                        <div className={`border-l-[3px] ${meta.rail} bg-linear-to-r ${meta.wash} to-transparent to-40%`}>
                          <BatchDetail r={r} meta={meta} hasValue={hasValue} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          </table>
        </div>

        {rows.length > 0 && (
          <div className="flex items-center justify-end border-t border-line px-3 py-2.5">
            <Pagination
              size="small"
              current={current}
              pageSize={pageSize}
              total={rows.length}
              showSizeChanger
              pageSizeOptions={[10, 20, 50, 100]}
              onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
              showTotal={(t, [from, to]) => `${from}–${to} of ${t} ${t === 1 ? 'batch' : 'batches'}`}
            />
          </div>
        )}
      </div>
      {data?.truncated && (
        <div className="mt-2 text-xs text-subtle">Showing the top 500 by value — the KPIs above still cover everything. Narrow with the filters to see the rest.</div>
      )}
    </div>
  );
}

// Expanded per-batch detail: full batch facts (left) + why-it-can't-be-sold + what-to-do (right).
function BatchDetail({ r, meta, hasValue }: { r: any; meta: { label: string; color: string; card?: string }; hasValue: boolean }) {
  const detail = reasonDetail(r);
  const detailRows: { label: string; value: any }[][] = [
    [{ label: 'Batch no', value: <CopyValue value={r.batchNo} label="Batch no"><CodeChip>{r.batchNo}</CodeChip></CopyValue> }, { label: 'Source', value: r.sourceBatchNo ? <CopyValue value={r.sourceBatchNo} label="Source batch"><CodeChip>{r.sourceBatchNo}</CodeChip></CopyValue> : null }],
    // No "past" badge on the date here either — the "Why it can't be sold" panel a few inches to the
    // right already says it in words ("Past its expiry date — expired N days ago").
    [{ label: 'Mfg date', value: fmtDate(r.mfgDate) }, { label: 'Exp date', value: fmtDate(r.expDate) }],
    [{ label: 'Warehouse', value: r.warehouseCode ? <span className="flex min-w-0 items-center gap-1.5"><CodeChip hue={entityHue(r.warehouseCode)} className="shrink-0">{r.warehouseCode}</CodeChip><span className="min-w-0 truncate text-muted">{r.warehouseName}</span></span> : (r.warehouseName || null) }, { label: 'On-hand', value: <span className="font-semibold tabular-nums">{qf(r.qty)}</span> }],
    [{ label: 'Received', value: qf(r.qtyIn) }, { label: 'Issued', value: qf(r.qtyOut) }],
    ...(hasValue ? [[{ label: 'Unit cost', value: inr2(r.unitCost) }, { label: 'Locked value', value: <span className="font-medium">{inr2(r.value)}</span> }]] : []),
    [{ label: 'SKU', value: r.sku }, { label: 'Added on', value: dt(r.createdAt) }],
    [{ label: 'Last updated', value: dt(r.updatedAt) }, null as any],
  ];
  return (
    <div className="px-5 py-4">
      <div className="grid gap-x-6 gap-y-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div>
          <ColHead>Batch details</ColHead>
          <DetailGrid rows={detailRows} />
        </div>
        <div>
          <ColHead>Why it can't be sold</ColHead>
          <div className={`rounded-control border p-3.5 shadow-e1 ${meta.card ?? 'border-line bg-surface'}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Pill color={meta.color}>{meta.label}</Pill>
              {r.status !== r.reason && <span className="text-xs text-subtle">batch status: {r.status}</span>}
            </div>
            <p className="mt-2.5 text-sm leading-relaxed text-content">{detail.what}</p>
            <div className="mt-3 flex items-start gap-2 rounded-control border border-line bg-surface px-3 py-2 text-sm text-muted">
              <Wrench size={14} className="mt-0.5 shrink-0" />
              <span>{detail.action}</span>
            </div>

            {/* Provenance — who put it in this state (return receiver) or the system flip job. */}
            {r.origin?.source === 'RETURN' && (
              <div className="mt-3 flex items-start gap-2 border-t border-line pt-2.5 text-sm text-muted">
                <UserCheck size={14} className="mt-0.5 shrink-0" />
                <span>
                  Booked in on return <span className="font-medium text-content">{r.origin.returnNo}</span>
                  {r.origin.condition && <> as {CONDITION_LABEL[r.origin.condition] ?? String(r.origin.condition).toLowerCase()}</>}
                  {r.origin.byName ? <> · flagged by <span className="font-medium text-content">{r.origin.byName}</span></> : ''}
                  {r.origin.at ? <> · {dt(r.origin.at)}</> : ''}
                </span>
              </div>
            )}
            {r.origin?.source === 'SYSTEM' && (
              <div className="mt-3 flex items-start gap-2 border-t border-line pt-2.5 text-sm text-muted">
                <Cog size={14} className="mt-0.5 shrink-0" />
                <span>Flagged automatically by the nightly expiry job{r.origin.at ? <> · {dt(r.origin.at)}</> : ''}</span>
              </div>
            )}

            {hasValue && (
              <div className="mt-3 flex items-baseline justify-between border-t border-line pt-2.5 text-sm">
                <span className="text-muted">Capital locked in this batch</span>
                <span className="font-semibold text-danger-600">{inr2(r.value)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ColHead({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 type-overline text-subtle">{children}</div>;
}

// Hairline key-value grid — brand-green label cells + plain value cells (matches the app's Descriptions look).
function DetailGrid({ rows }: { rows: { label: string; value: any }[][] }) {
  const labelCls = 'flex items-center bg-brand-50 px-3 py-1.5 type-label text-muted dark:bg-brand-900/25';
  const valueCls = 'flex items-center bg-surface px-3 py-1.5 text-sm text-content';
  const fmt = (v: any) => (v == null || v === '' ? <span className="text-subtle">—</span> : v);
  return (
    <div className="overflow-hidden rounded-control border border-line">
      <div className="grid gap-px bg-line sm:grid-cols-[minmax(84px,auto)_minmax(0,1fr)_minmax(84px,auto)_minmax(0,1fr)]">
        {rows.map((pair, i) => (
          <Fragment key={i}>
            <div className={labelCls}>{pair[0].label}</div>
            <div className={valueCls}>{fmt(pair[0].value)}</div>
            {pair[1] ? (
              <>
                <div className={labelCls}>{pair[1].label}</div>
                <div className={valueCls}>{fmt(pair[1].value)}</div>
              </>
            ) : (
              <>
                <div className="hidden bg-brand-50 dark:bg-brand-900/25 sm:block" />
                <div className="hidden bg-surface sm:block" />
              </>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/** "Blocked" maps to `sky`: expired already owns danger and QC hold owns warning, and a regulatory
 *  hold needs to stay tellable apart from both at a glance rather than collapsing into neutral. */
const KPI_TONE: Record<string, StatTone> = {
  'border-l-line': 'neutral',
  'border-l-danger-500': 'danger',
  'border-l-warning-500': 'warning',
  'border-l-violet-500': 'sky',
};

/** Delegates to the shared StatCard (see DispatchesPage). The old hand-rolled version was a bare
 *  <button>, which sizes to fit-content — so these tiles rendered at four different widths. */
function Kpi({ title, value, sub, accent, icon, active, onClick }: { title: string; value: string; sub?: string; accent: string; valueCls?: string; icon?: React.ReactNode; active?: boolean; onClick?: () => void }) {
  return (
    <StatCard
      label={title}
      value={value}
      hint={sub}
      tone={KPI_TONE[accent] ?? 'neutral'}
      icon={icon}
      onClick={onClick}
      active={active}
    />
  );
}
